/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Core concepts", description = "Understand the core building blocks of Hightouch — organizations, workspaces, user groups, roles, environments, and Spaces.", lastUpdatedDate = new Date(1790553600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Core concepts",
        description: "Understand the core building blocks of Hightouch — organizations, workspaces, user groups, roles, environments, and Spaces.",
        lastUpdatedDate: "Mon Sep 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Organizations",
          "depth": 0,
          "id": "organizations"
        }, {
          "value": "Workspaces",
          "depth": 0,
          "id": "workspaces"
        }, {
          "value": "Environments",
          "depth": 0,
          "id": "environments"
        }, {
          "value": "Spaces",
          "depth": 0,
          "id": "spaces"
        }, {
          "value": "User groups",
          "depth": 0,
          "id": "user-groups"
        }, {
          "value": "Roles",
          "depth": 0,
          "id": "roles"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      img: "img",
      hr: "hr",
      h2: "h2",
      strong: "strong",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["This page covers core concepts. For prescriptive guidance on when to use each\ncontrol and how to structure your organization, see ", _jsx(_components.a, {
            href: "/workspace-management/governance",
            children: "Governance best\npractices"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Hightouch is built around a set of core concepts: organizations, workspaces, user groups, roles, environments, and Spaces. Understanding how they relate to each other is the foundation for configuring your workspace correctly."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/overview-understanding-the-organizational-structure.jpg",
          alt: "Organizational structure showing the relationship between organizations, workspaces, user groups, and roles"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "organizations",
        children: "Organizations"
      }), "\n", _jsxs(_components.p, {
        children: ["An ", _jsx(_components.strong, {
          children: "organization"
        }), " is the top-level entity in Hightouch. It represents your entire company and is the container for billing, SSO configuration, and user management. All workspaces belong to a single organization."]
      }), "\n", _jsxs(_components.p, {
        children: ["Most companies should have exactly one organization. An organization is created automatically when the first person at your company signs up — that person becomes the first organization admin. From there, organization admins can manage billing, configure ", _jsx(_components.a, {
          href: "/workspace-management/sso",
          children: "SSO and SCIM"
        }), ", ", _jsx(_components.a, {
          href: "/workspace-management/user-management#invite-users",
          children: "invite users"
        }), ", and ", _jsx(_components.a, {
          href: "/workspace-management/user-management#transfer-organization-admin-access",
          children: "transfer admin access"
        }), " as team members change."]
      }), "\n", _jsxs(_components.p, {
        children: ["See: ", _jsx(_components.a, {
          href: "/workspace-management/workspaces#first-sign-in",
          children: "Set up an organization and workspace"
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "workspaces",
        children: "Workspaces"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "workspace"
        }), " is a fully isolated partition within an organization. Each workspace has its own sources, destinations, models, syncs, audiences, and user roster. By design, nothing is shared across workspace boundaries — not even the connection to your data source."]
      }), "\n", _jsx(_components.p, {
        children: "Workspaces are commonly used to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Separate staging and production environments"
        }), "\n", _jsx(_components.li, {
          children: "Isolate business units or regions that must not share resources or configuration"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For most enterprise customers, a single production workspace is sufficient. Hightouch's internal governance controls — including user groups, roles, subsets, and Spaces — allow different teams, regions, or brands to operate within a shared workspace without interfering with each other."
      }), "\n", _jsxs(_components.p, {
        children: ["To learn how to set up a new workspace, see ", _jsx(_components.a, {
          href: "/workspace-management/workspaces",
          children: "Workspaces"
        }), ". For guidance on when to create additional workspaces versus using ", _jsx(_components.a, {
          href: "/workspace-management/environments",
          children: "Environments"
        }), ", see ", _jsx(_components.a, {
          href: "/workspace-management/workspaces#do-you-need-another-workspace",
          children: "Do you need another workspace?"
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "environments",
        children: "Environments"
      }), "\n", _jsxs(_components.p, {
        children: ["An ", _jsx(_components.strong, {
          children: "environment"
        }), " is a linked copy of a workspace used for change management — typically dev, staging, and production. Environments let you test changes to models and syncs before deploying them to production, without needing a completely separate workspace."]
      }), "\n", _jsx(_components.p, {
        children: "Environments share the same organization, user roster, and governance controls as the parent workspace. Use them when you need a safe promotion path but don't need hard data isolation."
      }), "\n", _jsxs(_components.p, {
        children: ["See: ", _jsx(_components.a, {
          href: "/workspace-management/environments",
          children: "Environments"
        }), " | ", _jsx(_components.a, {
          href: "/workspace-management/workspaces#do-you-need-another-workspace",
          children: "Workspaces vs environments"
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "spaces",
        children: "Spaces"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "Space"
        }), " is a visibility boundary within a single workspace. Spaces let you scope which resources (syncs, models, audiences) a team can see without creating a separate workspace. They're useful when multiple teams share a workspace but shouldn't be distracted by each other's work."]
      }), "\n", _jsx(_components.p, {
        children: "Spaces control visibility, not permissions — roles and user groups still govern what actions a user can take."
      }), "\n", _jsxs(_components.p, {
        children: ["See: ", _jsx(_components.a, {
          href: "/workspace-management/spaces",
          children: "Spaces"
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "user-groups",
        children: "User groups"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "user group"
        }), " is a collection of users who share the same permissions. Access control in Hightouch is managed at the group level, not for individual users. This makes it easier to keep permissions consistent and to update access as team structures change."]
      }), "\n", _jsx(_components.p, {
        children: "User groups are created within an organization and can be granted access to one or more workspaces. Users can belong to multiple groups and inherit the combined permissions from all of their assigned groups."
      }), "\n", _jsxs(_components.p, {
        children: ["In most cases, user-to-group mappings are managed through your identity provider via ", _jsx(_components.a, {
          href: "/workspace-management/sso",
          children: "SSO and SCIM"
        }), ". If SSO is not configured, user groups and memberships can be managed manually in the Hightouch app."]
      }), "\n", _jsxs(_components.p, {
        children: ["See: ", _jsx(_components.a, {
          href: "/workspace-management/user-management",
          children: "User groups"
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "roles",
        children: "Roles"
      }), "\n", _jsxs(_components.p, {
        children: ["A ", _jsx(_components.strong, {
          children: "role"
        }), " defines what actions a user group can perform within a workspace. User groups aggregate users; roles define the scope of what those users can do."]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch offers two types of roles:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Pre-built roles"
            }), " — apply broadly to all resources in a workspace. These are the right starting point for most teams."]
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Admin"
              }), " — full grants across the workspace, typically assigned to data or IT teams"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Editor"
              }), " — can create and configure resources, but cannot delete or update existing source and destination configurations"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Viewer"
              }), " — read-only access across the workspace"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Draft Contributor"
              }), " — can create and propose changes, but cannot publish; all changes require approval before going live"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Custom roles"
            }), " — provide granular, per-resource control. Custom roles let you define specific permissions for individual sources, destinations, and Customer Studio parent models. Useful when pre-built roles are not flexible enough for a large or complex workspace."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See: ", _jsx(_components.a, {
          href: "/workspace-management/roles",
          children: "Roles"
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/workspaces",
            children: "Workspaces"
          }), " — create, manage, and delete workspaces"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/user-management",
            children: "Set up user groups and members"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/roles",
            children: "Configure roles"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/workspace-management/sso",
            children: "Set up SSO and SCIM"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/governance",
            children: "Governance best practices"
          }), " — when to use workspaces, subsets, Spaces, destination rules, approval flows, and more"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
